/**
 * IVP Redux Slice
 *
 * State management for Industry Viability Profile display.
 * Follows the oracleSlice.ts pattern.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { ivpApi } from '@services/ivpApi'
import type { IVPData, IVPVitalSign } from '@services/companyProfileApi'
import type { NodeWeights } from '@services/ivpApi'

// ============================================================================
// State Interface
// ============================================================================

export interface IVPState {
  // Current IVP data
  ivp: IVPData | null

  // Vital signs with actuals
  vitalSigns: IVPVitalSign[] | null

  // Node weights
  nodeWeights: NodeWeights

  // Loading states
  loading: boolean
  generating: boolean
  error: string | null

  // UI state
  expandedSections: Record<string, boolean>
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: IVPState = {
  ivp: null,
  vitalSigns: null,
  nodeWeights: {},
  loading: false,
  generating: false,
  error: null,
  expandedSections: {
    vital_signs: true,
    value_chain: true,
    common_pathologies: true,
    revenue_model: false,
    cost_structure: false,
    growth_levers: false,
    env_sensitivities: false,
  },
}

// ============================================================================
// Async Thunks
// ============================================================================

export const fetchIVP = createAsyncThunk(
  'ivp/fetchIVP',
  async (companyId: number) => {
    return await ivpApi.getIVP(companyId)
  }
)

export const generateIVP = createAsyncThunk(
  'ivp/generateIVP',
  async ({ companyId, naicsCode, revenueTier }: {
    companyId: number
    naicsCode?: string
    revenueTier?: string
  }) => {
    const result = await ivpApi.generateIVP(companyId, naicsCode, revenueTier)
    return result.ivp
  }
)

export const fetchVitalSigns = createAsyncThunk(
  'ivp/fetchVitalSigns',
  async (companyId: number) => {
    return await ivpApi.getVitalSigns(companyId)
  }
)

export const fetchNodeWeights = createAsyncThunk(
  'ivp/fetchNodeWeights',
  async (companyId: number) => {
    return await ivpApi.getNodeWeights(companyId)
  }
)

// ============================================================================
// Slice
// ============================================================================

const ivpSlice = createSlice({
  name: 'ivp',
  initialState,
  reducers: {
    clearIVP: (state) => {
      state.ivp = null
      state.vitalSigns = null
      state.nodeWeights = {}
      state.error = null
    },
    toggleSection: (state, action: PayloadAction<string>) => {
      const section = action.payload
      state.expandedSections[section] = !state.expandedSections[section]
    },
  },
  extraReducers: (builder) => {
    // fetchIVP
    builder
      .addCase(fetchIVP.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchIVP.fulfilled, (state, action) => {
        state.loading = false
        state.ivp = action.payload
      })
      .addCase(fetchIVP.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to load IVP'
      })

    // generateIVP
    builder
      .addCase(generateIVP.pending, (state) => {
        state.generating = true
        state.error = null
      })
      .addCase(generateIVP.fulfilled, (state, action) => {
        state.generating = false
        state.ivp = action.payload
      })
      .addCase(generateIVP.rejected, (state, action) => {
        state.generating = false
        state.error = action.error.message || 'IVP generation failed'
      })

    // fetchVitalSigns
    builder
      .addCase(fetchVitalSigns.fulfilled, (state, action) => {
        state.vitalSigns = action.payload?.metrics || null
      })

    // fetchNodeWeights
    builder
      .addCase(fetchNodeWeights.fulfilled, (state, action) => {
        state.nodeWeights = action.payload
      })
  },
})

export const { clearIVP, toggleSection } = ivpSlice.actions
export default ivpSlice.reducer
